import { ICertificateUser, ICertificateTotals, ICertificateCount } from 'types/reportsInterfaces'
import { SubAPI } from './API'
import { ICertificate, ISkillTranslate } from 'types/cvsInterfaces'

/**
 * Certificate API
 * @notExported
 */
class CertificateAPI extends SubAPI {
  /**
   * Get all certificates.
   *
   * @returns Array of certificates.
   */
  public getAll(controller?: AbortController): Promise<ICertificate[]> {
    return this.api.get(`certificates`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Update certificate.
   *
   * @param data - Certificate data.
   * @returns Updated certificate.
   */
  public putCertificate(data: { certificate: { id: number } }, controller?: AbortController): Promise<ICertificate> {
    return this.api.put(
      `certificates/${data.certificate.id}`,
      data,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Create certificate.
   *
   * @param body - Certificate data.
   * @returns Created certificate.
   */
  public createCertificate(
    body: { certificate: string; language: string },
    controller?: AbortController
  ): Promise<ICertificate> {
    return this.api.post(`certificates`, body, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Delete certificate.
   *
   * @param id - Certificate ID.
   * @returns Promise of certificate deletion.
   */
  public delete(id: number, controller?: AbortController): Promise<void> {
    return this.api.delete(`certificates/${id}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Merge certificates.
   *
   * @param newSkillId - New certificate ID.
   * @param oldSkills - Old certificate IDs.
   * @returns Promise of certificate merge.
   */
  public merge(newCertificateId: number, oldCertificates: number[], controller?: AbortController): Promise<void> {
    return this.api.put(
      `certificates/merge`,
      { newCertificateId, oldCertificates },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Merge several groups of certificates in one transaction: all or nothing.
   *
   * @param merges - Groups to merge.
   * @returns Promise of the merges.
   */
  public mergeBulk(
    merges: { newCertificateId: number; oldCertificates: number[] }[],
    controller?: AbortController
  ): Promise<void> {
    return this.api.put(`certificates/merge/bulk`, { merges }, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Translate certificate.
   *
   * @param data - Certificate translations data.
   * @returns Updated certificate.
   */
  public translate(data: ISkillTranslate, controller?: AbortController): Promise<ICertificate> {
    return this.api.put(`certificates/translate`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Split certificates.
   *
   * @param oldSkillId - Old certificate ID.
   * @param newSkillIds - New certificate IDs.
   * @returns Promise of certificate split.
   */
  public split(oldCertificateId: number, newCertificateIds: number[], controller?: AbortController): Promise<void> {
    return this.api.put(
      'certificates/split',
      { oldCertificateId, newCertificateIds },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get all certificate totals.
   *
   * @returns Certificate totals.
   */
  public getAllCertificateTotals(controller?: AbortController): Promise<ICertificateTotals[]> {
    return this.api.get(`reports/certificates/totals`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get certificate totals for a team.
   *
   * @param teamId - Team ID.
   * @returns Certificate totals.
   */
  public getTeamCertificateTotals(teamId: number, controller?: AbortController): Promise<ICertificateTotals[]> {
    return this.api.get(
      `reports/certificates/totals/teams/${teamId}`,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get all certificate users.
   *
   * @returns Certificate users.
   */
  public getAllCertificateUsers(controller?: AbortController): Promise<ICertificateUser[]> {
    return this.api.get(`reports/certificates/users`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get certificate users for a team.
   *
   * @param teamId - Team ID.
   * @returns Certificate users.
   */
  public getTeamCertificateUsers(teamId: number, controller?: AbortController): Promise<ICertificateUser[]> {
    return this.api.get(
      `reports/certificates/users/teams/${teamId}`,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get certificate counts for a network.
   *
   * @param networkId - Network ID
   * @returns Certificate counts.
   */
  public getCertificateCounts(networkId: number, controller?: AbortController): Promise<ICertificateCount[]> {
    return this.api.get(
      `reports/certificates/count/${networkId}`,
      controller ? { signal: controller.signal } : undefined
    )
  }
}

export const certificateAPI = new CertificateAPI()
